import { Link } from 'react-router-dom';
import { DocSection, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'before-you-begin', label: 'Before you begin' },
  { id: 'connect', label: 'Connect your agent' },
  { id: 'ask', label: 'Ask for what you want' },
  { id: 'review', label: 'Review & approve' },
  { id: 'result', label: 'The result lands in Premiere' },
];

export default function Body() {
  return (
    <>
      <DocSection id="before-you-begin" title="Before you begin">
        <p>
          Agentic editing runs on the same local ClipRadar as everything else — there&rsquo;s
          nothing extra to install on the ClipRadar side. You need:
        </p>
        <ul>
          <li>
            ClipRadar running on Windows with a supported{' '}
            <Link to="/docs/reference/system-requirements">NVIDIA GPU</Link> — an agent
            can&rsquo;t drive an app that isn&rsquo;t running.
          </li>
          <li>
            A <Link to="/docs/concepts/licensing">licensed</Link> copy of ClipRadar — search
            works the same whether the request comes from you or an agent.
          </li>
          <li>A library with some footage already analyzed, so there&rsquo;s something to find.</li>
          <li>An AI agent that speaks MCP — Claude Code, Claude Desktop, or Codex.</li>
        </ul>
      </DocSection>

      <DocSection id="connect" title="Connect your agent">
        <p>
          Open <strong>Settings &rarr; Agent (MCP)</strong> and copy the connect command shown
          there — a one-time <span style={kbd}>claude mcp add &hellip;</span> line. Paste it into
          your agent&rsquo;s terminal or config once and it&rsquo;s connected: the status in
          Settings switches to <strong>Connected</strong> and lists how many tools are available.
          Nothing about this leaves your machine — the MCP server runs locally, the same as the
          rest of ClipRadar.
        </p>
        <DocShot slug="agentic-editing-quickstart" shot="connect" alt="Settings → Agent (MCP), Connected status and the copyable claude mcp add command" />
      </DocSection>

      <DocSection id="ask" title="Ask for what you want">
        <p>
          Once connected, describe the edit in plain language — &ldquo;find the interview clips
          where Sarah talks about the budget and cut them into a short sequence.&rdquo; The agent
          runs discovery first (checking your library, loading the index), then works out a plan
          before touching anything.
        </p>
        <DocShot slug="agentic-editing-quickstart" shot="transcript" alt="A chat transcript showing the agent's discovery step and a written plan" />
      </DocSection>

      <DocSection id="review" title="Review & approve">
        <p>
          The agent shows you its plan — what it&rsquo;s about to search, verify, and build —
          before it acts. Read it, redirect it (&ldquo;only the last two interviews&rdquo;), or
          approve it. It also checks each candidate clip against a thumbnail before committing to
          the final cut, so you&rsquo;re not trusting a blind text match. See{' '}
          <Link to="/docs/concepts/agentic-editing">Agentic editing (MCP)</Link> for the full
          plan-first workflow.
        </p>
      </DocSection>

      <DocSection id="result" title="The result lands in Premiere">
        <p>
          If the ClipRadar panel is open in your Premiere project, the agent can build the
          sequence and drop it straight into your open project as a new sequence — no manual
          import. Without the panel open, it exports a timeline XML file instead, the same one
          you&rsquo;d get from{' '}
          <Link to="/docs/guides/build-a-rough-cut">building a rough cut</Link> by hand, ready to
          import.
        </p>
        <DocShot slug="agentic-editing-quickstart" shot="sequence" alt="An agent-built sequence in a Premiere Pro timeline" />
      </DocSection>
    </>
  );
}
